import { MAX_CHUNK_CHARS } from "./constants";

const STRONG_PUNCTUATION = /([。！？.!?]+['"“”‘’」』】]*\s*|\n+)/;
const WEAK_PUNCTUATION = /([，,；;：:、]+['"“”‘’」』】]*\s*)/;
const READABLE_CHARS = /[\p{L}\p{N}\p{Lo}]/u;

export function normalizeSegmentText(text: string): string {
  return text
    .replace(/(\r\n|\r|\n)+/g, "\n")
    .replace(/ +/g, " ")
    .replace(/\.{3,}/g, "…")
    .trim();
}

export function preprocessText(text: string): string {
  return normalizeSegmentText(text.replace(/^\d+$/gm, ""));
}

export function isTtsConvertible(text: string): boolean {
  return Boolean(text?.trim()) && READABLE_CHARS.test(text);
}

function splitKeepDelimiter(text: string, separator: RegExp): string[] {
  const parts = text.split(separator);
  const segments: string[] = [];

  for (let index = 0; index < parts.length; index += 2) {
    let segment = parts[index] ?? "";
    if (index + 1 < parts.length && parts[index + 1]) {
      segment += parts[index + 1];
    }

    segment = segment.trim();
    if (segment && isTtsConvertible(segment)) {
      segments.push(segment);
    }
  }

  return segments;
}

function splitByLength(text: string, maxChunkChars: number): string[] {
  const normalized = text.trim();
  if (normalized.length <= maxChunkChars) {
    return normalized ? [normalized] : [];
  }

  const chunks: string[] = [];
  let cursor = 0;

  while (cursor < normalized.length) {
    const next = normalized.slice(cursor, cursor + maxChunkChars).trim();
    if (next) {
      chunks.push(next);
    }
    cursor += maxChunkChars;
  }

  return chunks;
}

function splitSegment(segment: string, maxChunkChars: number): string[] {
  if (segment.length <= maxChunkChars) {
    return [segment];
  }

  const weakParts = splitKeepDelimiter(segment, WEAK_PUNCTUATION);
  if (weakParts.length > 1) {
    return weakParts.flatMap((part) => splitSegment(part, maxChunkChars));
  }

  return splitByLength(segment, maxChunkChars);
}

export function chunkText(
  input: string,
  maxChunkChars = MAX_CHUNK_CHARS,
): string[] {
  const processed = preprocessText(input);
  if (!processed) {
    return [];
  }

  const strongParts = splitKeepDelimiter(processed, STRONG_PUNCTUATION);
  const coarseSegments = strongParts.length > 0 ? strongParts : [processed];

  return coarseSegments.flatMap((segment) =>
    splitSegment(segment, maxChunkChars)
      .map(normalizeSegmentText)
      .filter(isTtsConvertible),
  );
}
